import css from './newscarousel.scss?inline';
import globalStyles from '../../index.scss?inline';
import { ArgSpecDictionary, TypedVars } from '../component-utils';
import swiperStyles from "../../../node_modules/swiper/swiper-bundle.min.css?inline";
import Swiper from 'swiper';
import { CarouselType } from '../Carousel/CarouselType';

const DEBUG_VERBOSE: boolean = false;
const CLASS_NAME: string = 'HHDSNewsCarousel';
const TAG_NAME: string = 'hhds-newscarousel';

export const HHDSNewsCarouselTagName: string = 'hhds-newscarousel';

// ////////////////////////////////////////////////////////////////////
export class HHDSNewsCarousel extends HTMLElement {
	private vars: TypedVars = new TypedVars(this);
	private shadow: ShadowRoot;

	private swiperContainer: HTMLElement | null = null;
	private swiperInstance: Swiper | null = null;

	private navVisible: boolean = false;

	private _prevButton: HTMLElement | null = null;
	private _nextButton: HTMLElement | null = null;

	private _postIds: string[] = [];

	private scrollContainerEl!: HTMLElement;
	private blockColumn1El!: HTMLElement;
	private blockTitleEl!: HTMLElement;
	private blockImageEl!: HTMLElement;

	constructor() {
		super();
		DEBUG_VERBOSE && console.log(CLASS_NAME, 'constructed');

		this.shadow = this.attachShadow({ mode: 'open' });

		if (!this.shadow) throw new Error('Unable to attach shadowRoot');

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

	}

	static get observedAttributes() {
		return Object.keys(ArgSpecs);
	}

	// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
	// Lifecycle Methods
	// https://developer.mozilla.org/en-US/docs/Web/Web_Components/Using_custom_elements#using_the_lifecycle_callbacks

	// Invoked each time the custom element is appended into a document-connected element.
	connectedCallback() {
		this.parseAttributes();

		this.render();

		requestAnimationFrame(() => {
			this.initializeSwiper();
		});

		window.addEventListener('resize', () => this.checkNavVisibility());

		DEBUG_VERBOSE && console.log(`[${TAG_NAME}] Initialised`);
	}

	checkNavVisibility(forceUpdate: boolean = false) {

		if (this.swiperInstance) {

			if (window.innerWidth >= 1024) {

				if (this.vars.get('nav') === 'false') {

					this.setNavVisibility(false, true);

				} else {

					const numSlides = typeof this.swiperInstance.slides.length === 'number' ? this.swiperInstance.slides.length : 0;
					const slidesInView = typeof this.swiperInstance.params.slidesPerView === 'number' ? this.swiperInstance.params.slidesPerView : numSlides;
					const visible = (slidesInView < numSlides);
					DEBUG_VERBOSE && console.log(`There is space for ${slidesInView} slide, and ${numSlides} slides`);
					DEBUG_VERBOSE && console.log(`Visibilty should be ${visible}`);
					this.setNavVisibility(visible, forceUpdate);

				}

			} else {

				this.setNavVisibility(false, true);

			}

		} else {

			DEBUG_VERBOSE && console.error("No swiper instance available");

		}

	}

	setNavVisibility(visible: boolean, forceUpdate: boolean) {

		if (this.navVisible !== visible || forceUpdate) {

			DEBUG_VERBOSE && console.log(`Setting nav visibility to ${visible}`);

			this.navVisible = visible;
			const nav = this.shadow.querySelector('hhds-buttongroup') as HTMLElement;
			if (nav) {
				nav.style.display = visible ? 'flex' : 'none';
			}
		}

	}

	parseAttributes() {
		this.vars.parse(this, ArgSpecs);
		DEBUG_VERBOSE && console.log(`this.vars`, this.vars);
	}

	render() {
		
		const link = this.vars.get('link');
		DEBUG_VERBOSE && console.log('*** LINK: ', link);

		this.shadow.innerHTML = /* html */ `
		<style>${swiperStyles}</style>
		<style>${globalStyles}</style>
		<style>${css}</style>
		<div class="hhds-newscarousel container">
			<slot class="slides"></slot>
			<div class=" hhds-newscarouselnav" style="${this.vars.get("nav") === "false" ? "--margin-bottom: 0;" : ""}">
				<div class="grid">

					<div style="--rich-text-spacing-lg: 0px;" class="col-span-4  sm:col-span-7 md:col-span-8">
						<h2 class="heading heading--sans-01">${this.vars.get("title")}</h2>
					</div>	 


					<hhds-buttongroup class="col-span-2 ${!this.vars.get("link") ? `col-start-11 stats` : ""}" type="toolbar" orientation="horizontal">
						<hhds-button type="secondary" class="button-prev">
							<hhds-icon type="arrowleft" slot="start" style="--icon-color: var(--color-neutral-grey-900);"></hhds-icon>
						</hhds-button>
						<hhds-button type="secondary" class="button-next">
							<hhds-icon type="arrowright" slot="start" style="--icon-color: var(--color-neutral-grey-900);"></hhds-icon>
						</hhds-button>	
					</hhds-buttongroup>

					${this.vars.get("link") ? `
						<hhds-link class="col-span-2 col-start-11" href="${this.vars.get("link")}"><hhds-icon type="arrowright" slot="end"></hhds-icon>View All</hhds-link>
					`: ""}

				</div>
			</div>
		</div>

		<div class="wrapper hhds-newscarousel-slider container">
			<div class="swiper ${HHDSNewsCarouselTagName}">
				<div class="swiper-wrapper"></div>
				<!-- Add Pagination -->
				<div class="swiper-pagination"></div>
			</div>
		</div>
		`;


		this.swiperContainer = this.shadowRoot?.querySelector('.swiper') as HTMLElement;

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
		// Copy all elements in the slot into .swiper-wrapper

		const slidesEl = this.shadowRoot?.querySelector('.slides');
		const swiperWrapper = this.shadowRoot?.querySelector('.swiper-wrapper');

		if (slidesEl && slidesEl instanceof HTMLSlotElement) {

			const elements = slidesEl.assignedElements();

			DEBUG_VERBOSE && console.log(`elements`, elements);

			elements.forEach((element) => {
				element.classList.add('swiper-slide');
				swiperWrapper?.appendChild(element);
			});
		}

		this._prevButton = this.shadow.querySelector('.button-prev');
		this._nextButton = this.shadow.querySelector('.button-next');



		// Add our grid classes to the slot elements
		const assignedNodes = swiperWrapper?.childNodes;
		assignedNodes?.forEach(node => {

			if (node.nodeType === Node.ELEMENT_NODE) {
				const element = node as HTMLElement;
				this._postIds.push(element.getAttribute('post') || '');
			}
		});


	}

	// Invoked each time the custom element is disconnected from the document's DOM.
	disconnectedCallback() {
		this.swiperDestroy();
	}

	swiperDestroy() {
		if (this.swiperInstance) {
			this.swiperInstance.destroy(true, true);
		}
	}

	// Invoked each time the custom element is moved to a new document.
	adoptedCallback() { }

	// Invoked each time one of the custom element's attributes is added, removed, or changed.
	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		DEBUG_VERBOSE && console.log(`Attribute ${name} has changed from ${oldValue} to ${newValue}.`);

		/**
		 * Commented out as causing issue with some default attributes triggering
		 * attributeChangedCallback and the followup code not working as expected.
		 * The swiper instance was being removed and not re-instantiated correctly.
		 * Also unsure as to why some attributes were triggering attributeChangedCallback
		 */

		// this.swiperDestroy();
		// 	this.parseAttributes();
		// 	this.render();
		// 	this.initializeSwiper();
	}

	initializeSwiper() {

		const slidesPerView = this.vars.get<number>(ArgSpecsKey.SlidesPerView) || 3;
		const speed = this.vars.get<number>(ArgSpecsKey.TransitionSpeed);

		if (!this.swiperContainer) throw new Error('No swiperContainer');

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		this.swiperInstance = new Swiper(this.swiperContainer, {
			loop: false,
			// Temp disabled pagination
			// pagination: {
			// 	el: this.swiperContainer.querySelector('.swiper-pagination') as HTMLElement,
			// },
			slideFullyVisibleClass: 'swiper-slide-visible',
			slidesPerView: 1.1,
			spaceBetween: 16, 
			speed,
			breakpoints: {
				// when window width is >=
				640: {
					slidesPerView: 2,
					spaceBetween: 16,
				},
				1024: {
					slidesPerView: 3,
					spaceBetween: 20,
				},
				// when window width is >=
				1440: {
					slidesPerView: slidesPerView,
					spaceBetween: 24,
				},
			},
			on: {
				init: () => {
					this.setButtonDisabledState();
				},
				afterInit: () => {
					requestAnimationFrame(() => {
						this.handleVisibleSlides();
					})
				},				
				slideChange: (swiper: Swiper) => {
					this.setButtonDisabledState();
					this.handleVisibleSlides();
					DEBUG_VERBOSE && console.log('slideChange', swiper);
				},
				resize: () => {
					this.handleVisibleSlides();
				},
			},
		});

		this.swiperInstance.init();

		this._nextButton?.addEventListener('click', () => {
			this.swiperInstance?.slideNext();
		});

		this._prevButton?.addEventListener('click', () => {
			this.swiperInstance?.slidePrev();
		});

		this.checkNavVisibility(true);

	}

	setButtonDisabledState() {
		if (this.swiperInstance?.isBeginning) {
			this._prevButton?.setAttribute('disabled', 'true');
		} else {
			this._prevButton?.removeAttribute('disabled');
		}

		if (this.swiperInstance?.isEnd) {
			this._nextButton?.setAttribute('disabled', 'true');
		} else {
			this._nextButton?.removeAttribute('disabled');
		}
	}

	handleVisibleSlides() {
		if (!this.swiperInstance) throw new Error('No this.swiperInstance!');

		const { realIndex, slides } = this.swiperInstance;

		const slidesPerView = this.swiperInstance.params.slidesPerView as number;

		slides.forEach((slide, index) => {
			const min = realIndex;
			const max = realIndex + slidesPerView;
			const state = index >= min && index < max;

			slide.classList.toggle('swiper-slide-visible', state);
		});
	}
    
// ////////////////////////////////////////////////////////////////////

  get blockTitleHeight(){
    return this.blockTitleEl.clientHeight + 24;
  }

  get blockImageHeight(){
    return this.blockImageEl.clientHeight;
  }

    isOverflowing() {

        // If mobile size then we want to return true so the CSS uses the default relative position for the `block-title`
        if (window.innerWidth < 640) return true;

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

        const computedStyle = window.getComputedStyle(this.blockColumn1El);

        const { paddingBottom, paddingTop } = computedStyle;

        const columnPaddingTop = parseFloat(paddingTop);
        const columnPaddingBottom = parseFloat(paddingBottom);

        const leftColContentHeight = columnPaddingTop + this.blockImageHeight + this.blockTitleHeight + columnPaddingBottom; // Excluding gap between title and image

        const delta = this.scrollContainerEl.clientHeight - leftColContentHeight;

        return delta < 0;
    }

    // ////////////////////////////////////////////////////////////////////

    updateImageTopProperty(){

        const computedStyle = window.getComputedStyle(this.blockColumn1El);

        const { paddingBottom } = computedStyle;

        const imagePaddingBottom = parseFloat(paddingBottom);
        
        const imageTop = this.scrollContainerEl.clientHeight - this.blockImageEl.clientHeight - imagePaddingBottom;

        this.style.setProperty('--image-top', `${imageTop}px`);

        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

        const imageTopPortrait = this.blockTitleHeight + 48;

        this.style.setProperty('--image-top-portrait', `${imageTopPortrait}px`);
        
        // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
        
        const imageTopOverflow = this.scrollContainerEl.clientHeight - this.blockImageHeight - imagePaddingBottom;

        this.style.setProperty('--image-top-overflow', `${imageTopOverflow}px`);
    }

}

export const enum ArgSpecsKey {
	SlidesPerView = 'slidesPerView',
	TransitionSpeed = 'transitionSpeed',
	Type = 'type',
	Title = 'title',
	Nav = 'nav',
	Link = 'link',	
}

export const ArgSpecs: ArgSpecDictionary = {
	[ArgSpecsKey.Type]: {
		description: `The type of carousel.`,
		defaultValue: CarouselType.Community,
		type: CarouselType,
	},
	[ArgSpecsKey.Title]: {
		description: `The title of the carousel.`,
		defaultValue: '',
		type: String,
	},
	[ArgSpecsKey.Link]: {
		description: `The URL of the View All page.`,
		defaultValue: '',
		type: String,
	},
	[ArgSpecsKey.Nav]: {
		description: `Whether or not to show the navigation.`,
		defaultValue: 'true',
		type: String,
	},
	[ArgSpecsKey.SlidesPerView]: {
		description: `Number of slides per view (slides visible at the same time on slider's container).`,
		defaultValue: 1,
		type: Number,
	},

	[ArgSpecsKey.TransitionSpeed]: {
		description: `Duration of transition between slides (in ms)`,
		defaultValue: 300,
		type: Number,
	},
};
